import React, { useState, useEffect } from 'react';
import { userAPI, limitsAPI } from '../services/api';
import { responseData } from '../services/apiResponse';
import { AlertCircle, Info } from 'lucide-react';
import Profile from '../pages/Profile';
import IdentityVerificationSettings from './IdentityVerificationSettings';

const TransactionSettings = () => {
  const [transferLimits, setTransferLimits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    loadUserInfo();
    loadTransferLimits();
  }, []);

  const loadTransferLimits = async () => {
    try {
      const response = await limitsAPI.getLimits();
      setTransferLimits(responseData(response) || null);
    } catch (err) {
      console.error('Failed to load transfer limits:', err);
    }
  };

  const loadUserInfo = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getCurrentUser();
      const user = responseData(response);
      setUserInfo(user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load user info');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-700"></div>
        <p className="mt-4 text-slate-600">Loading settings…</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Profile embedded />
      <IdentityVerificationSettings />
      <section className="border-t border-slate-200 pt-8" aria-labelledby="transaction-limits-title">
        <div className="mb-6">
          <h1 id="transaction-limits-title" className="text-lg font-semibold text-slate-900">Transaction Limits</h1>
          <p className="mt-1 text-sm text-slate-600">Set your yearly and monthly transaction limits</p>
        </div>

        {error && (
        <div className="mb-6 flex items-start rounded border border-red-200 bg-red-50 p-4" role="status" aria-live="polite">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-semibold">Error</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
        )}

        {transferLimits && (
        <div className="mb-6 rounded border border-slate-200 bg-slate-50 p-4">
          <h3 className="mb-3 flex items-center gap-2 font-semibold text-slate-800">
            <Info className="w-4 h-4" />
            Transaction Limits
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded border border-slate-200 bg-white p-3">
              <p className="text-gray-500 text-xs mb-1">Per Transaction</p>
              <p className="font-bold text-lg text-gray-900">${parseFloat(transferLimits.per_transaction).toFixed(2)}</p>
            </div>
            <div className="rounded border border-slate-200 bg-white p-3">
              <p className="text-gray-500 text-xs mb-1">Per Month</p>
              <p className="font-bold text-lg text-gray-900">${parseFloat(transferLimits.per_month).toFixed(2)}</p>
            </div>
            <div className="rounded border border-slate-200 bg-white p-3">
              <p className="text-gray-500 text-xs mb-1">Per Year</p>
              <p className="font-bold text-lg text-gray-900">${parseFloat(transferLimits.per_year).toFixed(2)}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Maximum limits allowed by the system. Your personal limits cannot exceed these values.
          </p>
        </div>
        )}

        {userInfo && (
        <div className="space-y-4">
          <div className="rounded border border-cyan-200 bg-cyan-50 p-4">
            <h3 className="mb-3 font-semibold text-slate-800">Current Usage</h3>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Yearly Sent:</span>
                  <span className="text-sm font-semibold text-gray-800">
                    ${userInfo.yearly_spent_usd?.toFixed(2) || '0.00'} / ${userInfo.yearly_limit_usd?.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Yearly Remaining:</span>
                  <span className="text-sm font-semibold text-green-600">
                    ${((userInfo.yearly_limit_usd || 0) - (userInfo.yearly_spent_usd || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="border-t border-cyan-200 pt-3">
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Monthly Sent:</span>
                  <span className="text-sm font-semibold text-gray-800">
                    ${userInfo.monthly_spent_usd?.toFixed(2) || '0.00'} / ${userInfo.monthly_limit_usd?.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Monthly Remaining:</span>
                  <span className="text-sm font-semibold text-green-600">
                    ${((userInfo.monthly_limit_usd || 0) - (userInfo.monthly_spent_usd || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
        )}
      </section>
    </div>
  );
};

export default TransactionSettings;
